import {
  Button,
  Checkbox,
  DialogContent,
  Dialogß,
  Inputß,
  Label,
  Selectß,
  type SelectßOption,
} from '@otter-ai/den-components';
import { useTranslations } from 'next-intl';
import { type ReactNode, useMemo, useRef, useState } from 'react';
import { TRANSCRIPT_EXPORT_SETTINGS_FIELD_DEFINITIONS } from '@/constants/export';
import type {
  BooleanFieldDefinition,
  FileFormatConfig,
  FileFormatSettings,
  NumberFieldDefinition,
} from '@/shared/types/export';

type SettingViewData = { key: string; input: ReactNode; label: string };

export type ExportSettingsModalProps = {
  /**
   * The list of file formats and their options.
   */
  fileFormatConfigs: FileFormatConfig[];
  /**
   * A map from `formatKey` to the user's selected settings for that format.
   */
  fileFormatSettings: FileFormatSettings;
  onCancel: () => void;
  onSave: (update: FileFormatSettings) => void; // TODO: What is saved?
};

export function ExportSettingsModal({
  onCancel,
  onSave,
  fileFormatConfigs,
  fileFormatSettings,
}: ExportSettingsModalProps) {
  useAssertFileFormatConfig(fileFormatConfigs);

  const t = useTranslations('exports.settingsModal');
  const formatOptions = useFileFormatOptions(fileFormatConfigs);

  // --- State management ---
  const [selectedFormatKey, setSelectedFormatKey] = useState<string>(
    () =>
      fileFormatSettings.selectedFormatKey ??
      (formatOptions[0].value as string),
  );
  const [optionValues, setOptionValues] = useState(
    fileFormatSettings.optionValues,
  );
  const [isChanged, setIsChanged] = useState(false);

  // --- Event handlers ---
  const handleFormatChange = (formatKey: string) => {
    setSelectedFormatKey(formatKey);
    setIsChanged(true);
  };
  const handleOptionChange = (optionKey: string, value: boolean | number) => {
    setOptionValues((prev) => ({
      ...prev,
      [optionKey]: value,
    }));
    setIsChanged(true);
  };
  const handleSave = () => {
    onSave({
      selectedFormatKey: selectedFormatKey as string,
      optionValues,
    });
  };

  const currentFormatConfig = fileFormatConfigs.find(
    (config) => config.formatKey === selectedFormatKey,
  );

  return (
    <Dialogß
      data-testid="export-settings-modal"
      title={t('title')}
      size="sm"
      action={null}
      Footer={
        <div className="flex flex-row gap-2">
          <Button variant="outlineNeutral" version="legacy" onClick={onCancel}>
            {t('cancelButton')}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={!isChanged}>
            {t('saveButton')}
          </Button>
        </div>
      }
    >
      <ModalLayout
        text={{
          fileFormat: t('fileFormat'),
          selectPlaceholder: t('selectPlaceholder'),
        }}
        fileFormatSelect={{
          options: formatOptions,
          value: selectedFormatKey,
          onChange: handleFormatChange,
        }}
        settings={
          currentFormatConfig
            ? generateViewDataForSettings({
                formatConfig: currentFormatConfig,
                onOptionChange: handleOptionChange,
                settingValues: optionValues,
              })
            : []
        }
      />
    </Dialogß>
  );
}

/**
 * Presentational component for the modal's content layout.
 */
function ModalLayout({
  text,
  settings,
  fileFormatSelect,
}: {
  text: {
    fileFormat: string;
    selectPlaceholder: string;
  };
  fileFormatSelect: {
    options: SelectßOption[];
    value: string;
    onChange: (value: string) => void;
  };
  settings: SettingViewData[];
}) {
  return (
    <DialogContent className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-2">
        <div>{text.fileFormat}</div>
        <Selectß
          {...fileFormatSelect}
          placeholder={text.selectPlaceholder}
          variant="outlineNeutral"
          version="legacy"
          shape="pill"
        />
      </div>
      <div className="flex flex-col gap-4 min-h-[184px]">
        {settings.map(({ key, input, label }) => (
          <div key={key}>
            <Label control={input}>{label}</Label>
          </div>
        ))}
      </div>
    </DialogContent>
  );
}

/**
 * Create the options for the file format select from the file format configs.
 *
 * @example
 * ```tsx
 * const formatOptions = useFileFormatOptions(fileFormatConfigs);
 *
 * return <Selectß options={formatOptions} onChange={...}/>;
 * ```
 */
function useFileFormatOptions(configs: FileFormatConfig[]): SelectßOption[] {
  return useMemo(() => {
    return configs.map((config) => ({
      value: config.formatKey,
      label: config.label,
    }));
  }, [configs]);
}

/**
 * An input for integer values.
 *
 * UX behavior:
 * - Only allow integer numbers (no decimals) as input.
 * - When the user deletes the content of the input:
 *   - The previous value is shown as a placeholder.
 *   - For onBlur, the value is set to the previous value.
 *   - The value in `allSettingValues` is still set to the previous value.
 * - A max and min value is enforced.
 */
function IntegerInput({
  name,
  allSettingValues,
  fieldDefinition,
  onChange,
}: {
  name: string;
  allSettingValues: Record<string, boolean | number>;
  fieldDefinition: NumberFieldDefinition;
  onChange: (name: string, value: boolean | number) => void;
}) {
  const [controlledValue, setControlledValue] = useState<number | null>(() =>
    clampNumberValue(allSettingValues[name] as number, fieldDefinition),
  );

  return (
    <Inputß
      type="number"
      className="text-xs min-w-[7em]"
      name={name}
      placeholder={allSettingValues[name]?.toString() ?? ''}
      value={controlledValue ?? ''}
      step={1}
      onChange={(e) => {
        e.stopPropagation();
        if (!e.target.value) {
          setControlledValue(null);
          return;
        }
        const value = Number.parseInt(e.target.value, 10);
        const clampedValue = clampNumberValue(value, fieldDefinition);
        setControlledValue(clampedValue);
        onChange(name, clampNumberValue(value, fieldDefinition));
      }}
      onBlur={() => {
        setControlledValue(allSettingValues[name] as number);
      }}
      max={fieldDefinition.max}
      min={fieldDefinition.min}
    />
  );
}

// ===== View data generation functions =====

function generateViewDataForSettings({
  formatConfig,
  onOptionChange,
  settingValues,
}: {
  formatConfig: FileFormatConfig;
  settingValues: Record<string, boolean | number>;
  onOptionChange: (optionKey: string, value: boolean | number) => void;
}): SettingViewData[] {
  const fieldSettings = formatConfig.optionDefinitions.map((option) =>
    generateViewDataForIndividualSetting({
      name: option.value,
      label: option.label,
      onChange: onOptionChange,
      allSettingValues: settingValues,
    }),
  );

  const filteredFieldSettings = fieldSettings.filter<SettingViewData>(
    (setting): setting is SettingViewData => setting !== null,
  );

  return filteredFieldSettings;
}

function generateViewDataForIndividualSetting({
  name,
  label,
  onChange,
  allSettingValues,
}: {
  name: keyof typeof TRANSCRIPT_EXPORT_SETTINGS_FIELD_DEFINITIONS;
  label: string;
  onChange: (name: string, value: boolean | number) => void;
  allSettingValues: Record<string, boolean | number>;
}): SettingViewData | null {
  const fieldDefinition = TRANSCRIPT_EXPORT_SETTINGS_FIELD_DEFINITIONS[name];

  if (!fieldDefinition) {
    throw new UnknownFieldNameError(name);
  }

  const showIf = fieldDefinition.showIf;
  if (showIf && !allSettingValues[showIf]) {
    return null;
  }
  const fieldType = fieldDefinition.type;

  const input = (() => {
    switch (fieldType) {
      case 'boolean':
        return (
          <Checkbox
            name={name}
            checked={maybeNegateValue(
              Boolean(allSettingValues[name]),
              fieldDefinition,
            )}
            onCheckedChange={(checked) => {
              if (typeof checked !== 'boolean') {
                throw new CheckboxEventIsNotBooleanError(name);
              }
              onChange(name, maybeNegateValue(checked, fieldDefinition));
            }}
          />
        );
      case 'integer':
        return (
          <IntegerInput
            name={name}
            allSettingValues={allSettingValues}
            fieldDefinition={fieldDefinition}
            onChange={onChange}
          />
        );
      default:
        return null;
    }
  })();

  if (!input) {
    throw new UnknownFieldTypeError(fieldType);
  }

  return {
    key: name,
    input,
    label,
  };
}

// ====== Utility functions ======

/**
 * Assert that the file format configs are valid.
 * @throws {EmptyFileFormatConfigsError} If no file format configs are provided.
 * @throws {MissingFormatKeyError} If a file format config is missing a format key.
 * @throws {DuplicateFormatKeyError} If a file format config has a duplicate format key.
 */
function useAssertFileFormatConfig(configs: FileFormatConfig[]) {
  const lastRef = useRef<FileFormatConfig[]>([]);

  if (lastRef.current === configs) {
    // Only check when configs changes
    return;
  }
  lastRef.current = configs;

  if (configs.length === 0) {
    throw new EmptyFileFormatConfigsError();
  }

  for (const config of configs) {
    if (!config.formatKey) {
      throw new MissingFormatKeyError(config);
    }
  }

  const formatKeys = new Set<string>();
  for (const config of configs) {
    if (formatKeys.has(config.formatKey)) {
      throw new DuplicateFormatKeyError(config);
    }
    formatKeys.add(config.formatKey);
  }
}

function maybeNegateValue(
  value: boolean,
  fieldDefinition: BooleanFieldDefinition,
) {
  if (fieldDefinition.negate) {
    return !value;
  }
  return value;
}

function clampNumberValue(
  value: number | undefined,
  fieldDefinition: NumberFieldDefinition,
) {
  const min = fieldDefinition.min ?? Number.NEGATIVE_INFINITY;
  const max = fieldDefinition.max ?? Number.POSITIVE_INFINITY;
  return Math.max(min, Math.min(max, value ?? 0));
}

// ====== Error classes ======

class EmptyFileFormatConfigsError extends Error {
  name = 'EmptyFileFormatConfigsError';
  constructor() {
    super('No file format configs provided');
  }
}

class MissingFormatKeyError extends Error {
  name = 'MissingFormatKeyError';
  constructor(public config: FileFormatConfig) {
    super('File format config must have a format key');
  }
}

class DuplicateFormatKeyError extends Error {
  name = 'DuplicateFormatKeyError';
  constructor(public config: FileFormatConfig) {
    super('Duplicate file format keys provided');
  }
}

class UnknownFieldTypeError extends Error {
  name = 'UnknownFieldTypeError';
  constructor(public fieldType: string) {
    super(`Unknown field type: ${fieldType}`);
  }
}

class CheckboxEventIsNotBooleanError extends Error {
  name = 'CheckboxEventIsNotBooleanError';
  constructor(public fieldName: string) {
    super(`Checkbox event for ${fieldName} is not a boolean`);
  }
}

class UnknownFieldNameError extends Error {
  name = 'UnknownFieldNameError';
  constructor(public fieldName: string) {
    super(`Unknown field name: ${fieldName}`);
  }
}
